/**
 * Admin Layout - Matching Figma Design
 * Left sidebar with navigation, no header
 * Mobile: Sidebar as drawer overlay
 */

import { Link, useLocation } from "react-router"
import { FileText, Users, ChevronRight, ArrowLeft, MessageSquare, Menu, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useState } from "react"

interface AdminLayoutProps {
  children: React.ReactNode
}

const adminNavItems = [
  {
    id: "templates",
    label: "Templates",
    to: "/admin/templates",
    icon: FileText,
  },
  {
    id: "prompts",
    label: "AI Prompts",
    to: "/admin/prompts",
    icon: MessageSquare,
  },
  {
    id: "users",
    label: "User & Access",
    to: "/admin/users",
    icon: Users,
  },
]

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation()
  const pathname = location.pathname
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false)
    }
  }

  return (
    <div className="wm-shell-bg h-screen flex overflow-hidden">
      {/* Mobile header with hamburger */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white/80 backdrop-blur-xl border-b border-surface-border flex items-center px-4 z-30">
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="w-10 h-10 flex items-center justify-center hover:bg-brand-100 rounded-lg transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="ml-3 text-lg font-medium text-gray-900">Administration</h1>
      </div>

      {/* Mobile overlay backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Left side - White background extending to left edge */}
      <div className={cn(
        "wm-panel shrink-0 h-screen flex border-r border-surface-border/70",
        // Mobile: fixed overlay, slide in from left
        "fixed md:relative inset-y-0 left-0 z-50 md:z-auto",
        "transform transition-transform duration-300 ease-in-out md:transform-none",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}>
        {/* Sidebar content - fixed 256px width */}
        <div className="w-64 flex flex-col h-full overflow-y-auto">
          {/* Mobile close button */}
          <div className="md:hidden flex items-center justify-between p-4 border-b border-surface-border">
            <span className="font-medium text-gray-900">Menu</span>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-secondary"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Header (desktop) */}
          <div className="hidden md:block p-6 border-b border-surface-border/70">
            <Link
              to="/dashboard"
              className="text-gray-600 hover:text-navy mb-4 flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Projects
            </Link>
            <h1 className="font-display text-xl font-semibold text-navy mt-4">Administration</h1>
          </div>

          {/* Back link (mobile) */}
          <div className="md:hidden p-4 border-b border-surface-border">
            <Link
              to="/dashboard"
              onClick={handleNavClick}
              className="text-gray-600 hover:text-navy flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Projects
            </Link>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4">
            <div className="space-y-1">
              {adminNavItems.map((item) => {
                const Icon = item.icon
                const isActive = pathname.startsWith(item.to)

                return (
                  <Link
                    key={item.id}
                    to={item.to}
                    onClick={handleNavClick}
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors",
                      isActive
                        ? "bg-[linear-gradient(120deg,#25115b_0%,#512ab2_55%,#7c4af7_100%)] text-white shadow-soft-lg"
                        : "text-gray-700 hover:bg-brand-100/80"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Icon
                        className={cn(
                          "w-5 h-5",
                          isActive ? "text-white" : "text-gray-400"
                        )}
                      />
                      <span className="text-base font-medium">{item.label}</span>
                    </div>
                    {isActive && <ChevronRight className="w-4 h-4" />}
                  </Link>
                )
              })}
            </div>
          </nav>
        </div>
      </div>

      {/* Right side - Content area, children control their own backgrounds */}
      <div className="flex-1 h-screen overflow-hidden flex flex-col pt-14 md:pt-0">
        {children}
      </div>
    </div>
  )
}
